
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { PlaceHolderImages } from '@/lib/placeholder-images';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LocationCard } from '@/components/locations/LocationCard';
import { BookMarked, Heart, Edit } from 'lucide-react';
import { format } from 'date-fns';
import { useUser, useDoc, useFirestore, useMemoFirebase } from '@/firebase';
import { NotLoggedIn } from '@/components/profile/NotLoggedIn';
import { ProfileSkeleton } from '@/components/profile/ProfileSkeleton';
import type { LoggedSwim, UserProfile as UserProfileType, Loch } from '@/app/lib/types';
import { doc } from 'firebase/firestore';
import { getLochBySlug } from '@/app/lib/lochs';
import { EditProfileDialog } from '@/components/profile/EditProfileDialog';
import { Button } from '@/components/ui/button';

function FavoriteSpots({ favoriteSpotIds }: { favoriteSpotIds: string[] }) {
  const [favoriteSpots, setFavoriteSpots] = useState<Loch[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchFavoriteSpots() {
      if (favoriteSpotIds.length === 0) {
        setIsLoading(false);
        return;
      }
      
      const spotPromises = favoriteSpotIds.map(id => getLochBySlug(id));
      const spots = (await Promise.all(spotPromises)).filter((s): s is Loch => s !== undefined);
      setFavoriteSpots(spots);
      setIsLoading(false);
    }
    fetchFavoriteSpots();
  }, [favoriteSpotIds]);

  if (isLoading) {
    return <p className="text-muted-foreground">Loading favorite spots...</p>;
  }

  if (favoriteSpots.length === 0) {
    return <p className="text-muted-foreground">No favorite spots saved yet. Explore the swim spots and add some!</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {favoriteSpots.map((location) => (
        <LocationCard key={location.id} location={location} />
      ))}
    </div>
  );
}

function SwimLog({ loggedSwims }: { loggedSwims: LoggedSwim[] }) {
    if (loggedSwims.length === 0) {
        return <p className="text-muted-foreground">No swims logged yet. Get out there and make a splash!</p>;
    }

    const sortedSwims = [...loggedSwims].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return (
        <div className="space-y-3 sm:space-y-4 md:space-y-6">
            {sortedSwims.map((swim, index) => (
              <Card key={index} className="overflow-hidden">
                <CardHeader>
                  <CardTitle>{swim.locationName}</CardTitle>
                  <p className="text-sm text-muted-foreground">{format(new Date(swim.date), 'MMMM d, yyyy')}</p>
                </CardHeader>
                {swim.notes && (
                    <CardContent>
                        <div className="prose prose-sm max-w-none text-foreground">
                            <blockquote className="border-l-4 border-accent pl-4 italic">
                            {swim.notes}
                            </blockquote>
                        </div>
                    </CardContent>
                )}
              </Card>
            ))}
        </div>
    );
}

export default function ProfilePage() {
  const avatarImage = PlaceHolderImages.find((img) => img.id === 'profile-avatar');
  const { user, isUserLoading } = useUser();
  const navigate = useNavigate();
  const firestore = useFirestore();

  const userProfileRef = useMemoFirebase(() => {
    if (!user) return null;
    return doc(firestore, 'users', user.uid);
  }, [user, firestore]);
  
  const { data: userProfile, isLoading: isProfileLoading } = useDoc<UserProfileType>(userProfileRef);

  useEffect(() => {
    if (!isUserLoading && !user) {
      navigate('/login?redirect_to=/profile');
    }
  }, [isUserLoading, user, navigate]);

  const isLoading = isUserLoading || isProfileLoading;

  if (isLoading) {
    return <ProfileSkeleton />;
  }
  
  if (!user || !userProfileRef) {
      return (
        <div className="container mx-auto">
            <NotLoggedIn />
        </div>
      )
  }

  return (
    <div className="container mx-auto max-w-5xl">
      <div className="flex flex-col md:flex-row items-center gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8 md:mb-12">
        <Avatar className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 border-2 sm:border-4 border-primary">
          {user.photoURL ? (
            <AvatarImage src={user.photoURL} alt={user.displayName || 'User'} />
          ) : avatarImage && (
            <AvatarImage src={avatarImage.imageUrl} alt={user.displayName || ''} data-ai-hint={avatarImage.imageHint} />
          )}
          <AvatarFallback>{user.email?.[0].toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-grow text-center md:text-left">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">{user.displayName || 'Wild Swimmer'}</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">{user.email}</p>
        </div>
        <EditProfileDialog user={user} userProfileRef={userProfileRef}>
            <Button variant="outline" className="w-full md:w-auto text-sm">
                <Edit className="mr-2 h-4 w-4" />
                Edit Clan Details
            </Button>
        </EditProfileDialog>
      </div>

      <div className="space-y-6 sm:space-y-8 md:space-y-12">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold flex items-center gap-2 mb-3 sm:mb-4 md:mb-6">
            <Heart className="h-5 w-5 sm:h-6 sm:w-6 text-accent" />
            Favorite Spots
          </h2>
          <FavoriteSpots favoriteSpotIds={userProfile?.favoriteSpots || []} />
        </div>

        <div>
          <h2 className="text-xl sm:text-2xl font-semibold flex items-center gap-2 mb-3 sm:mb-4 md:mb-6">
            <BookMarked className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            Swim Log
          </h2>
          <SwimLog loggedSwims={userProfile?.loggedSwims || []} />
        </div>
      </div>
    </div>
  );
}
